import { useRef, useState, type FormEvent } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { virtualAccountsApi, type VirtualAccountListItem } from '@/api/virtualAccounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const NAME_MAX_LENGTH = 80;
const TITLE_ID = 'rename-virtual-account-title';
const HELP_ID = 'rename-virtual-account-help';

interface RenameVirtualAccountDialogProps {
  /** The owned virtual account being renamed (a list row or a detail); the dialog mounts per target (keyed by the caller). */
  virtualAccount: Pick<VirtualAccountListItem, 'id' | 'name'>;
  onClose: () => void;
  onRenamed: () => void | Promise<void>;
}

type RenameError = 'nameTaken' | 'forbidden' | 'generic' | null;

export function RenameVirtualAccountDialog({
  virtualAccount,
  onClose,
  onRenamed,
}: RenameVirtualAccountDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(virtualAccount.name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<RenameError>(null);
  // The name field takes focus once the dialog has entered (`autoFocus` is a lint error here).
  const nameRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();

  const { dirty } = useDirty(name);
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmed.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await virtualAccountsApi.update(virtualAccount.id, { name: trimmed });
      await onRenamed();
      onClose();
    } catch (caught) {
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === 'VIRTUAL_ACCOUNT_NAME_TAKEN') setError('nameTaken');
      else if (code === ErrorCodes.FORBIDDEN) setError('forbidden');
      else setError('generic');
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={<DialogSubtitle id={HELP_ID}>{t('virtualAccounts.renameHelp')}</DialogSubtitle>}
      subtitleId={HELP_ID}
      fullWidth
      maxWidth="xs"
      slotProps={{ transition: { onEntered: () => nameRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{t('virtualAccounts.renameTitle')}</DialogTitle>
        <DialogContent>
          {error === 'generic' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('virtualAccounts.errorGeneric')}
            </Alert>
          )}
          <TextField
            label={t('virtualAccounts.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputRef={nameRef}
            required
            fullWidth
            error={error === 'nameTaken' || error === 'forbidden'}
            helperText={
              error === 'nameTaken'
                ? t('virtualAccounts.errorNameTaken')
                : error === 'forbidden'
                  ? t('virtualAccounts.errorForbidden')
                  : undefined
            }
            slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={trimmed.length === 0} loading={submitting}>
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
